import sum from "hash-sum";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";

import { ensureArray } from "@app/service/helpers";
import {
  TSPVMsgMarkedState,
  TSPVMsgReadState,
  TSPVUState,
} from "@app/types/insider";

import { extractFileName } from "../routines/file-sys-ops";
import { schema } from "../schema";

import type { AppState } from "../xstore";
import type { IFileItem } from "@app/types";
import type { TFileSysHead, TFileSysContent } from "@app/types/insider/fs";
export const selectUiMainSlice = createSelector(
  schema.uiSpvMain.select,
  (s) => s
);

export const selectCurrentSpvMessage = createSelector(
  schema.uiSpvMain.select,
  (s) => {
    return s;
  }
);

function createParameterSelector(selector: any) {
  return (_: AppState, params: any) => selector(params);
}
const getFilesByArchivesFileHeadId = createParameterSelector(
  (params: { FSHID: number }) => params?.FSHID || null
);

const getFXMID = createParameterSelector(
  (params: { FXMID: number }) => params?.FXMID || null
);

const getUpdateParams = createParameterSelector(
  //table: 'spvMessages', key:bounds_key, bounds_start, bounds_end}
  (params: {
    table: string;
    key: string;
    bounds_start: number;
    bounds_end: number;
  }) => params
);
const createVirtualDir = ({
  name,
  size,
  vfDate,
}: {
  name: string;
  size?: number;
  vfDate?: Date;
}): IFileItem => {
  return {
    name,
    isDirectory: true,
    items: [],
    size: size || 0,
    dateModified: new Date(vfDate).toISOString(),
    thumbnail: "",
  };
};
const createVirtualFile = ({
  name,
  size,
  vfDate,
}: {
  name: string;
  size: number;
  vfDate: Date;
}): IFileItem => {
  return {
    name,
    isDirectory: false,
    size: size || 0,
    dateModified: vfDate ? new Date(vfDate).toDateString() : null,
    thumbnail: "",
  };
};

const contentIdx = createSelector(
  schema.fileSysContent.selectTableAsList,
  (content) => {
    // goupby content by FSH_ID
    return content.reduce((acc, rec) => {
      if (!rec.FSH_ID) {
        return acc;
      }
      if (!acc[rec.FSH_ID]) {
        acc[rec.FSH_ID] = [];
      }
      acc[rec.FSH_ID].push(rec);
      return acc;
    }, {});
  }
);

const spvFSHIdx = createSelector(
  schema.SPVMessages.selectTableAsList,
  (spv) => {
    return spv.reduce((acc, rec) => {
      if (!rec.FSH_ID) {
        return acc;
      }
      acc[rec.FSH_ID] = rec;
      return acc;
    }, {});
  }
);

const emptyFs = () => {
  const zipfile = createVirtualDir({
    name: "Original.zip",
    size: 0,
    vfDate: new Date(),
  });

  const prepared = createVirtualDir({
    name: "Prelucrat",
    size: 0,
    vfDate: new Date(),
  });

  return [zipfile, prepared];
};

export const invoiceSubir = (msg) => {
  const {
    QMY_CIF: ourCif,
    QRESULT_TYPE: oType,
    REFERINTA_INTERNA: oSerialNo,
    FXM_ID: oFxmId,
  } = msg;

  const typeMapping = {
    "FACTURA TRIMISA": "tspvClient",
    "FACTURA PRIMITA": "tspvSupplier",
    "ERORI FACTURA": "tspvError",
  };
  const spvType = typeMapping[oType] || "tspvClientInProgress"; // Default to 'tspvClientInProgress' if not matched

  return encodeSubdir(oFxmId, spvType, oSerialNo);
};

function normDirName(str) {
  // Remove invalid characters for directory names
  let result = str
    .replace(/ /g, "")
    .replace(/-/g, "")
    .replace(/\./g, "")
    .replace(/\*/g, "")
    .replace(/&/g, "")
    .replace(/\//g, "")
    .replace(/\\/g, "")
    .replace(/</g, "")
    .replace(/>/g, "")
    .replace(/:/g, "")
    .replace(/"/g, "")
    .replace(/\|/g, "")
    .replace(/\?/g, "");

  // Trim trailing spaces and periods which are not allowed at the end of directory names
  result = result.trim(); // Removes leading and trailing spaces
  while (
    result.length > 0 &&
    (result.charAt(result.length - 1) === "." ||
      result.charAt(result.length - 1) === " ")
  ) {
    result = result.slice(0, -1);
  }

  return result;
}

const encodeSubdir = (iFxmId, iType, iSerialNo) => {
  const typeToPrefix = {
    tspvClientInProgress: "",
    tspvClient: "",
    tspvSupplier: "FZ",
    tspvError: "ER",
  };

  const prefix = typeToPrefix[iType] || "";
  const key = ["tspvSupplier", "tspvError"].includes(iType) ? `${iFxmId}_` : "";
  const suffix = iSerialNo && iSerialNo !== "null" ? iSerialNo : "necunoscut";
  return prefix + key + normDirName(suffix);
};
const realFilesofCif = createSelector(
  schema.fsDirTree.select,
  schema.uiSpvMain.select,
  (fTree, uiMain) => {
    const CIF = (uiMain.currentRecord?.QMY_CIF || "").replace("RO", "").trim();
    const subDir = invoiceSubir(uiMain.currentRecord);
    return fTree[CIF]?.[subDir] || [];
  }
);

export const _selectFileStructure = createSelector(
  schema.SPVMessages.selectTable,
  schema.fileSysHead.selectTable,
  spvFSHIdx,
  contentIdx,
  schema.uiInvoiceOverall.select,
  schema.fsDirTree.select,
  schema.uiSpvMain.select,
  (spvMsgTable, vfhead, fshIdx, vffiles, uiState, fTree, uiMain) => {
    const fshid = uiMain.currentRecord.FSH_ID;
    if (!fshid) {
      return emptyFs();
    }
    if (!uiState?.selectedCIF) {
      return emptyFs();
    }
    const fileSys = [] as IFileItem[];
    const head = vfhead[`${fshid}`] as TFileSysHead;
    if (!head) {
      return emptyFs();
    }
    const msgRef =
      head.META_SOURCE === "SPM"
        ? spvMsgTable?.[fshIdx?.[head?.SPM_ID]?.FXM_ID]
        : null; // OTHER FILE SOURCES , NOT IMPLEMENTED YET

    if (!msgRef) {
      return emptyFs();
    }
    const zipfile = createVirtualDir({
      name: "Original.zip",
      size: head.FILE_SIZE,
      vfDate: new Date(head.INSERTED),
    });

    const content = vffiles[fshid];
    // console.log('content', content);
    if (content?.length) {
      content.forEach((rec: TFileSysContent) => {
        const file = createVirtualFile({
          name: rec.F_NAME,
          size: rec.F_SIZE,
          vfDate: new Date(head.INSERTED),
        });
        zipfile.items.push(file);
      });
    }
    fileSys.push(zipfile);
    const prepared = createVirtualDir({
      name: "Prelucrat",
      size: 0,
      vfDate: new Date(),
    });

    const CIF = uiState.selectedCIF.replace("RO", "").trim();
    const subDir = invoiceSubir(msgRef);
    const realFilesRepo = fTree[CIF]?.[subDir] || [];
    const theFiles00 = realFilesRepo.filter((rec) => !rec.isDirectory);
    // adjust the structure:
    const realFiles = theFiles00.map((rec) => {
      return createVirtualFile({
        name: extractFileName(rec.filePath),
        size: rec.size,
        vfDate: undefined,
      });
    });
    if (Array.isArray(realFiles) && realFiles.length) {
      for (const realFile of realFiles) {
        prepared.items.push(realFile);
      }
    }
    fileSys.push(prepared);
    if (fileSys.length === 0) {
      return emptyFs();
    }
    return fileSys;
  }
); // ?? ((_, params) => params?.FSHID || '0');

export const selectFileStructure = createSelector(
  _selectFileStructure,
  (s) => s
);

export const selectFindPdfOfFXMID = createCachedSelector(
  schema.fileSysLocalDisk.selectTable,
  schema.fsDirTree.select,
  schema.SPVMessages.selectTable,
  getFXMID,
  (hdsys, fsTree, messages, fxmid) => {
    const uploadID = messages[fxmid]?.QREQUEST_ID;
    if (!uploadID) {
      return null;
    }
    const ourRef = messages[fxmid]?.REFERINTA_INTERNA;
    const ourCif = messages[fxmid]?.QMY_CIF;
    const ourDir = fsTree[ourCif]?.[ourRef];
    if (!ourDir) {
      return null;
    }
    const pdf = ensureArray(ourDir).find((file) =>
      (file.filePath || "").includes(`_UID${uploadID}DIU_.pdf`)
    );
    if (!pdf) {
      return null;
    }
    return pdf.fullPath || pdf.filePath;
  }
)((_, params) => params?.FXMID || "0");

export const selectAreUnknonwContractors = createSelector(
  schema.SPVMessages.selectTableAsList,
  (s) => {
    return s.some((rec) => !rec.REFERINTA_INTERNA || !rec.DENUMIRE_PARTENER);
  }
);

export const selectCurrentMessagePartnersCIF = createSelector(
  schema.uiSpvMain.select,
  (main) => {
    const { currentRecord } = main;
    if (!currentRecord) {
      return null;
    }
    const ctrctorCIF =
      currentRecord.QRESULT_TYPE === "FACTURA PRIMITA"
        ? currentRecord.MATCHED_EMITENT
        : currentRecord.MATCHED_BENEFICIAR;
    const ctrctorCIFNORO = (ctrctorCIF || "").replace("RO", "").trim();
    return ctrctorCIFNORO;
  }
);

export const selectMessageType = createSelector(
  schema.uiSpvMain.select,
  (main) => {
    const { currentRecord } = main;
    if (!currentRecord) {
      return null;
    }
    return currentRecord.QRESULT_TYPE;
  }
);
const rawByFxmId = createSelector(
  schema.SPVInvoiceRaw.selectTableAsList,
  (s) => {
    return s.reduce((acc, rec) => {
      acc[rec.FXM_ID] = rec;
      return acc;
    }, {});
  }
);

export const currentInvoiceGlimpse = createSelector(
  schema.uiSpvMain.select,
  rawByFxmId,
  (s, raw) => {
    const { currentRecord } = s;

    if (!currentRecord) {
      return {};
    }
    return raw[currentRecord.FXM_ID] || {};
  }
);

export const selectCurrentSpvMessages = createSelector(
  schema.SPVMessages.selectTableAsList,
  schema.uiInvoiceOverall.select,
  (s, g) => {
    const gnoro = g.selectedCIF.replace("RO", "").trim();
    return s.filter((rec) => rec.QMY_CIF === gnoro);
  }
);

const idxFMusToFxm = createSelector(
  schema.SPVMessageUserState.selectTableAsList,
  (s) => {
    return s.reduce((acc, rec) => {
      acc[rec.FXM_ID] = rec;
      return acc;
    }, {});
  }
);
const idxFXMToFMus = createSelector(
  schema.SPVMessageUserState.selectTableAsList,
  (s) => {
    return s.reduce((acc, rec) => {
      if (acc[rec.FXM_ID]) {
        acc[rec.FXM_ID].push(rec);
      } else {
        acc[rec.FXM_ID] = [rec];
      }

      return acc;
    }, {});
  }
);

const idxFXMAmaToFMus = createSelector(
  schema.SPVMessageUserState.selectTableAsList,
  (s) => {
    return s.reduce((acc, rec) => {
      const key = `${rec.FXM_ID}:${rec.AMA_ID}`;
      acc[key] = rec;
      return acc;
    }, {});
  }
);

const empty = {
  FMUS_ID: -1,
  FXM_ID: 0,
  AMA_ID: 0,
  READ_STATE: "NOT_READ" as TSPVMsgReadState,
  MARKED_STATE: "NO_MARK" as TSPVMsgMarkedState,
  MESSAGE_NOTE: "",
  FXM_QDATA: "",
  FXM_QMYCIF: "",
} as TSPVUState;
export const currentInvoiceUserState = createSelector(
  schema.uiSpvMain.select,
  idxFMusToFxm,
  schema.whoami.select,
  (s, idx, who) => {
    const { currentRecord } = s;
    if (!currentRecord) {
      return {};
    }
    if (idx[currentRecord.FXM_ID]) {
      return idx[currentRecord.FXM_ID];
    }

    return {
      ...empty,
      FXM_ID: currentRecord.FXM_ID,
      AMA_ID: who.AmaId,
      FXM_QDATA: currentRecord.QDATA_CREARE,
      FXM_QMYCIF: currentRecord.QMY_CIF,
    } as TSPVUState;
  }
);

// select(s=>selectUserFlags(s, {FXM_ID:change.key, AMA_ID:who.AmaId}));
export const selectUserFlags = createCachedSelector(
  idxFXMAmaToFMus,
  (_, params) => params,
  (s, { FXM_ID, AMA_ID }) => {
    const key = `${FXM_ID}:${AMA_ID}`;
    return s[key] || empty;
  }
)((_, params) => sum(params));

const stateIdxByfxmQdata = createSelector(
  schema.SPVMessageUserState.selectTableAsList,
  (t) => {
    return t.reduce((acc, item) => {
      if (!item.FXM_QDATA) {
        return acc;
      }
      if (!acc[item.FXM_QDATA]) {
        acc[item.FXM_QDATA] = [item.FMUS_ID];
        return acc;
      }
      acc[item.FXM_QDATA].push(item.FMUS_ID);
      return acc;
    }, {});
  }
);
const intervalParamSelector = createParameterSelector(
  (params: { bounds_start: number; bounds_end: number }) => params
);

/* -------------------------------------------------------------------------- */

export const _deprecate_spvUserMessagesInRange = createCachedSelector(
  stateIdxByfxmQdata,
  intervalParamSelector,
  (idx, { schemaTable, bounds_start, bounds_end }) => {
    const result = [];
    for (let i = bounds_start; i <= bounds_end; i++) {
      if (idx[i]) {
        result.push(...idx[i]);
      }
    }
    return result;
  }
)((_, params) => `${params.bounds_start}-${params.bounds_end}`);

export const spvUserMessagesInRange = createCachedSelector(
  schema.SPVMessageUserState.selectTable,
  getUpdateParams,
  (state, queryParams) => {
    const { schemaTable, key, bounds_start, bounds_end } = queryParams;
    return Object.entries(state)
      .filter(([k, v]) => v[key] >= bounds_start && v[key] <= bounds_end)
      .map(([k, v]) => k);
  }
)((_, p) => JSON.stringify(p));

export const selectSpvMessagesRange = createCachedSelector(
  schema.SPVMessages.selectTable,
  getUpdateParams,
  (state, queryParams) => {
    const { schemaTable, key, bounds_start, bounds_end } = queryParams;
    return Object.entries(state)
      .filter(([k, v]) => v[key] >= bounds_start && v[key] <= bounds_end)
      .map(([k, v]) => k);
  }
)((_, p) => JSON.stringify(p));

export const selectSpvMessagesContentRange = createCachedSelector(
  schema.fileSysContent.selectTable,
  getUpdateParams,
  (state, queryParams) => {
    const { schemaTable, key, bounds_start, bounds_end } = queryParams;
    return Object.entries(state)
      .filter(([k, v]) => v[key] >= bounds_start && v[key] <= bounds_end)
      .map(([k, v]) => k);
    // it should return the keys of the content (FCC_ID)
  }
)((_, p) => JSON.stringify(p));

export const selectSpvMessagesBodyRange = createCachedSelector(
  schema.SPVInvoiceRaw.selectTable,
  getUpdateParams,
  (state, queryParams) => {
    const { tabschemaTablele, key, bounds_start, bounds_end } = queryParams;
    return Object.entries(state)
      .filter(([k, v]) => v[key] >= bounds_start && v[key] <= bounds_end)
      .map(([k, v]) => k);
    // it should return the keys of the content (FRAW_ID)
  }
)((_, p) => JSON.stringify(p));

export const selectSpvMessagesHeadRange = createCachedSelector(
  schema.fileSysHead.selectTable,
  getUpdateParams,
  (state, queryParams) => {
    const { schemaTable, key, bounds_start, bounds_end } = queryParams;
    return Object.entries(state)
      .filter(([k, v]) => v[key] >= bounds_start && v[key] <= bounds_end)
      .map(([k, v]) => k);
    // it should return the keys of the content (FFS_ID)
  }
)((_, p) => JSON.stringify(p));

export const selectSpvMessagesKeysRange = createCachedSelector(
  schema.SPVMessageKeys.selectTable,
  getUpdateParams,
  (state, queryParams) => {
    const { schemaTable, key, bounds_start, bounds_end } = queryParams;
    return Object.entries(state)
      .filter(([k, v]) => v[key] >= bounds_start && v[key] <= bounds_end)
      .map(([k, v]) => k);
    // it should return the keys of the content (FMK_ID)
  }
)((_, p) => JSON.stringify(p));
